import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';

const SESSION_STORAGE_KEY = 'hamzabenz_analytics_session';
const DEBOUNCE_MS = 120;

function generateSessionKey() {
  const arr = new Uint8Array(24);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < arr.length; i += 1) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

function parseUtm(search) {
  if (!search || typeof search !== 'string') return {};
  const q = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(q);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
  };
}

/**
 * يرسل مشاهدات الصفحات إلى الـ API الداخلي (بدون تتبع في صفحات الإدارة).
 */
export default function AnalyticsCollector() {
  const location = useLocation();
  const sessionKeyRef = useRef(null);
  const pageStartRef = useRef(Date.now());
  const lastRef = useRef({ path: null, search: '' });
  const debounceRef = useRef(null);
  const isFirstRef = useRef(true);

  useEffect(() => {
    try {
      let k = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!k || k.length < 8) {
        k = generateSessionKey();
        localStorage.setItem(SESSION_STORAGE_KEY, k);
      }
      sessionKeyRef.current = k;
    } catch {
      sessionKeyRef.current = generateSessionKey();
    }
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      return undefined;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const path = location.pathname || '/';
      const searchRaw = location.search || '';
      const search = searchRaw.startsWith('?') ? searchRaw.slice(1) : searchRaw;
      const prev = lastRef.current;
      const now = Date.now();
      const durationMs = Math.min(Math.max(0, now - pageStartRef.current), 86400000);

      const events = [];

      if (!isFirstRef.current && prev.path != null && durationMs > 0) {
        events.push({
          type: 'timing',
          path: prev.path,
          duration_ms: durationMs,
        });
      }

      const utm = parseUtm(searchRaw);
      const ev = {
        type: 'pageview',
        path,
        search,
        title: typeof document !== 'undefined' ? document.title : '',
        referrer: isFirstRef.current && typeof document !== 'undefined' ? document.referrer || '' : '',
        ...utm,
      };
      events.push(ev);

      lastRef.current = { path, search };
      pageStartRef.current = now;
      isFirstRef.current = false;

      const key = sessionKeyRef.current;
      if (!key || events.length === 0) return;

      const body = { session: key, events };
      api
        .post('/analytics/collect/', body)
        .catch(() => {});
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [location.pathname, location.search]);

  return null;
}
